import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertErrorCircle } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { kMaxFolderNameLength } from '@/user_files/folders/constants'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import FloatLabelFormInput from '@/matter/components/matter-dialogs/FloatLabelFormInput'
import { useCreateFolder } from '@/user_files/folders/hooks/useCreateFolder'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import { selectFolderDataById } from '@/user_files/folders/store/selectors'

const CreateFolderSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Folder name is required')
    .max(kMaxFolderNameLength, `Folder name must be ${kMaxFolderNameLength} characters or less`)
    .refine((val) => !hasInvalidUserFilesNameCharacter(val), kInvalidUserFilesNameMessage),
})

type CreateFolderForm = z.infer<typeof CreateFolderSchema>

type CreateFolderModalProps = {
  open: boolean
  parentFolderId: string
  onClose: () => void
  onFolderCreated?: (folderId: string) => void
}

export function CreateFolderModal({ open, parentFolderId, onClose, onFolderCreated }: CreateFolderModalProps) {
  const dispatch = useAppDispatch()
  const parentFolder = useAppSelector((state) => selectFolderDataById(state, { folderId: parentFolderId }))

  const { createFolder, isLoading: isSubmitting, error: submitError, reset: resetCreateFolder } = useCreateFolder()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateFolderForm>({
    resolver: zodResolver(CreateFolderSchema),
    mode: 'onChange',
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (open) {
      reset({ name: '' })
      resetCreateFolder()
    }
  }, [open, reset, resetCreateFolder])

  const onSubmit = async (data: CreateFolderForm) => {
    const folder = await createFolder(parentFolder ?? parentFolderId, data.name)

    if (folder) {
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Created folder: ${folder.name}`, durationMs: 3000 }))
      onFolderCreated?.(folder.id)
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <ModalContent
        title="New Folder"
        width="sm"
        height="max-sm"
        stacked
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: onClose }}
            primaryAction={{ label: 'Create', loading: isSubmitting, onClick: handleSubmit(onSubmit) }}
          />
        }
      >
        <FloatLabelFormInput id="folder-name" label="Folder Name" required error={errors.name?.message} registration={register('name')} />

        {submitError && (
          <p className="p-2 mt-3 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
            <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
            {submitError}
          </p>
        )}
      </ModalContent>
    </Modal>
  )
}
